import { isFlagOn, type FlagName } from "@henlo/feature-flags";

export interface TickerItem {
  text: string;
  link?: string;
}

/**
 * Hibernation flavor for the lobby marquee. Every line is audit surface —
 * keep the set tight and grounded. Each line below either states a hard
 * truth (BGT discontinued, withdraws remain) or sets the room's tone
 * (lights down, doors unlocked) using established Eileen canon.
 *
 * Future replacement: route this through a curated vocabulary bank +
 * Herald composition so the copy is governed, not generated. Until then,
 * keep this set ≤ 3 lines and avoid promises ("we'll be back," timelines,
 * commitments to action).
 */
const HIBERNATION_FLAVOR: readonly TickerItem[] = [
  { text: "🌑 ARCADE DARK · 1 HENLO = 1 MOONDUST" },
  { text: "📉 BGT REWARDS DISCONTINUED · WITHDRAWS REMAIN ON CHAIN" },
  { text: "🌙 NO HOURS · LIGHTS DOWN · DOORS UNLOCKED" },
] as const;

/**
 * Flags whose `userImpact` shifts the lobby into hibernation chrome. When any
 * of these is on, the ticker should route through `getHibernationTickerItems`.
 *
 * Source of truth is the `userImpact` field on each flag's manifest entry —
 * but we list the names explicitly here so the ticker can short-circuit
 * without walking the whole manifest at render time.
 */
const HIBERNATION_FLAGS: readonly FlagName[] = [
  "TETRIS_READS_DISABLED",
  "PRESENCE_DISABLED",
  "GALLERY_DISABLED",
  "BGT_REWARDS_DISCONTINUED",
];

/**
 * Returns true if the lobby is in hibernation mode (≥1 surface dark). The
 * marquee/ticker should swap to hibernation flavor when this is true.
 */
export function isLobbyHibernating(): boolean {
  return HIBERNATION_FLAGS.some((flag) => isFlagOn(flag));
}

/**
 * Hibernation flavor as ticker items. Caller decides whether to merge with
 * regular events (keep the BGT discontinuation banner first, then mix in
 * after-hours flavor) or replace them entirely.
 */
export function getHibernationTickerItems(): readonly TickerItem[] {
  return HIBERNATION_FLAVOR;
}
